import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { locationsTreeSelector } from '@app/domain/catalog';

interface CustomSelectAreaProps {
  selectedPrefecture: string[];
  setPrefecture: React.Dispatch<React.SetStateAction<string[]>>;
}

export function CustomSelectArea({
  selectedPrefecture,
  setPrefecture,
}: CustomSelectAreaProps) {
  const locations = useRecoilValue(locationsTreeSelector);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const renderSelectBoxContent = () => {
    const items: React.ReactNode[] = [
      <MenuItem key="all" value="">
        全国
      </MenuItem>,
    ];

    locations.forEach((region) => {
      items.push(
        <MenuItem
          key={region.id}
          value={region.id}
          style={{
            backgroundColor: '#f9f9f9',
            fontSize: 20,
            fontWeight: 'bold',
          }}
        >
          {region.name}
        </MenuItem>
      );

      region.children?.forEach((prefecture) => {
        items.push(
          <MenuItem
            key={prefecture.id}
            value={prefecture.id}
            style={{ paddingLeft: '30px' }}
          >
            {prefecture.name}
          </MenuItem>
        );
      });
    });

    return items;
  };

  return (
    <Select
      multiple
      sx={{ bgcolor: 'white', borderRadius: 2, px: 2, width: 250 }}
      open={isSelectOpen}
      onOpen={() => setIsSelectOpen(true)}
      onClose={() => setIsSelectOpen(false)}
      value={selectedPrefecture}
      name="prefecture"
      onChange={(event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as string[];
        const latestValue = value[value.length - 1];

        const selected = new Set<string>();

        if (isEmpty(latestValue)) {
          setPrefecture([]);
        } else {
          const region = locations.find((region) => region.id === latestValue);
          if (region) {
            region.children?.forEach((child) => selected.add(child.id));
          } else {
            selected.add(latestValue);
          }
          setPrefecture(Array.from(selected));
        }
        setIsSelectOpen(false);
      }}
      displayEmpty
      renderValue={() => {
        if (selectedPrefecture.length === 0) {
          return (
            <Typography fontSize={20} fontWeight={700}>
              全国
            </Typography>
          );
        }

        const selectedNames = locations.flatMap((region) => {
          const isRegionSelected = region.children?.every((child) =>
            selectedPrefecture.includes(child.id)
          );
          if (isRegionSelected) {
            return [region.name];
          }
          return (
            region.children
              ?.filter((child) => selectedPrefecture.includes(child.id))
              .map((child) => child.name) || []
          );
        });

        return (
          <Typography fontSize={20} fontWeight={700}>
            {selectedNames.join(', ')}
          </Typography>
        );
      }}
      MenuProps={{
        PaperProps: {
          style: {
            maxHeight: 300,
          },
        },
      }}
    >
      {renderSelectBoxContent()}
    </Select>
  );
}
